import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { BookButton } from "@/components/Buttons";
import { Reveal } from "@/components/Reveal";
import { formatDate } from "@/components/BlogTeaser";
import { useSEO } from "@/hooks/useSEO";
import { BLOG_SERVICE_MAP, SERVICES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  const service = SERVICES.find((s) => s.slug === BLOG_SERVICE_MAP[slug]);
  useSEO({
    title: post ? `${post.title} | Glebe Park Dental` : "Glebe Park Dental Blog",
    description: post?.excerpt || "",
    path: `/blog/${slug}`,
  });

  useEffect(() => {
    setPost(null);
    setMissing(false);
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setMissing(true));
  }, [slug]);

  return (
    <main className="bg-cream">
      <Nav />
      <section className="bg-sage pb-16 pt-36">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="group inline-flex items-center gap-2 font-sans text-sm text-fern transition-colors hover:text-forest"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Back to the blog
          </Link>
          {post && (
            <>
              <Reveal>
                <p className="mt-8 font-mono text-xs tracking-[0.25em] uppercase text-leafmute">
                  {post.category} · {formatDate(post.date)} · {post.read_time}
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h1 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
                  {post.title}
                </h1>
              </Reveal>
            </>
          )}
        </div>
      </section>

      {post && (
        <article className="py-16 lg:py-20" data-testid="blog-post-content">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <Reveal>
              <img
                src={post.image}
                alt={post.title}
                className="aspect-[16/8] w-full rounded-2xl object-cover shadow-xl"
              />
            </Reveal>
            <div className="mt-12">
              {post.content.map((block) =>
                block.type === "h2" ? (
                  <Reveal key={`${block.type}-${block.text.slice(0, 40)}`}>
                    <h2 className="mb-4 mt-10 font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                      {block.text}
                    </h2>
                  </Reveal>
                ) : (
                  <Reveal key={`${block.type}-${block.text.slice(0, 40)}`}>
                    <p className="mb-6 font-sans text-base leading-relaxed text-fern">{block.text}</p>
                  </Reveal>
                )
              )}
            </div>
            <Reveal>
              <div className="mt-14 flex flex-col items-start gap-6 rounded-2xl bg-forest p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
                <div>
                  <p className="font-mono text-xs tracking-[0.25em] uppercase text-leaf">
                    Glebe Park Dental · Canberra City
                  </p>
                  <h3 className="mt-2 font-serif text-2xl text-cream">
                    Want to chat it through in person?
                  </h3>
                  {service && (
                    <Link
                      to={`/services/${service.slug}`}
                      data-testid="blog-post-service-link"
                      className="group mt-3 inline-flex items-center gap-2 font-sans text-sm text-mist transition-colors hover:text-leaf"
                    >
                      Read our plain-English guide to {service.name.toLowerCase()}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  )}
                </div>
                <BookButton variant="light" testid="blog-post-book-button" className="shrink-0" />
              </div>
            </Reveal>
          </div>
        </article>
      )}

      {missing && (
        <div className="py-32 text-center" data-testid="blog-post-not-found">
          <p className="font-serif text-2xl text-ink">Hmm, we can't find that one.</p>
          <Link to="/blog" className="mt-4 inline-block font-sans text-sm text-leafdeep underline">
            Back to the blog
          </Link>
        </div>
      )}
      <Footer />
    </main>
  );
}
